/* eslint-disable max-lines */
import { useMemo } from 'react';
import { EElementCategory, EFieldType, EStaticType, IScheme } from '../types/calculator';
import { useComponentLayout } from './useComponentLayout';
import {
  EBenefitProgram,
  ECalculateType,
  EEmploymentType,
  EIncomeConfirmation,
  EMortgageType,
  EObjectType,
  ERealEstateType,
} from '../types/calculator/enums';
import { MAX_VALUE_IN_MONTH } from '../constants/max_value_in_month';
import {
  calculatePercentage,
  isApartmentsOrCarPlace,
  isFlatOrUnselected,
  isNewBuilding,
  isRefinance,
  isSecondary,
} from '../utils';

/**
 * Хук для получения схемы формы
 */
export const useGetFormScheme = (): IScheme => {
  // const { config } = useApplicationContext();
  const layout = useComponentLayout();

  // TODO: стоит завести другую RS настройку
  // const downPaymentOptions = config.getStrict<number[]>('mortgage-broker-calculator-frontend.downPaymentOptions');
  const downPaymentOptions = [10, 20, 30, 40];

  return useMemo(
    () =>
      ({
        elements: [
          {
            category: EElementCategory.Group,
            elements: [
              {
                category: EElementCategory.Field,
                label: 'Тип расчета',
                type: EFieldType.Chips,
                name: 'calculateType',
                source: [
                  { label: 'По стоимости недвижимости', value: ECalculateType.CostOfRealEstate },
                  { label: 'По доходу', value: ECalculateType.Income },
                ],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Регион объекта',
                type: EFieldType.Suggest,
                name: 'region',
                placeholder: 'Москва',
                // обогащается после получения данных с сервера
                source: [],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Тип кредита',
                type: EFieldType.Chips,
                name: 'mortgageType',
                source: [
                  { label: 'Ипотека', value: EMortgageType.Mortgage },
                  { label: 'Рефинансирование', value: EMortgageType.Refinancing },
                ],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Цель кредита',
                type: EFieldType.Chips,
                name: 'realEstateType',
                source: [
                  { label: 'Строящийся объект', value: ERealEstateType.NewBuilding },
                  { label: 'Готовый объект', value: ERealEstateType.Secondary },
                  { label: 'Строительство дома', value: ERealEstateType.HouseBuilding },
                ],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Тип недвижимости',
                type: EFieldType.Chips,
                name: 'objectType',
                source: state => [
                  {
                    label: 'Квартира',
                    value: EObjectType.Flat,
                    disabled: state.realEstateType === ERealEstateType.HouseBuilding,
                  },
                  {
                    label: 'Апартаменты',
                    value: EObjectType.Apartments,
                    disabled: state.realEstateType === ERealEstateType.HouseBuilding,
                  },
                  {
                    label: 'Дом',
                    value: EObjectType.House,
                    disabled: state.realEstateType === ERealEstateType.NewBuilding,
                  },
                  {
                    label: 'Машиноместо',
                    value: EObjectType.CarPlace,
                    disabled: state.realEstateType === ERealEstateType.HouseBuilding,
                  },
                ],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Совокупный ежемесячный доход',
                type: EFieldType.InputNumber,
                name: 'income',
                rightAdornment: '₽',
                placeholder: '150 000',
                hidden: state => state.calculateType !== ECalculateType.Income,
                validation: {
                  min: {
                    message: 'Доход не должен быть меньше 0',
                    value: 0,
                  },
                  required: 'Поле должно быть заполнено',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Стоимость объекта',
                placeholder: '12 000 000',
                type: EFieldType.InputNumber,
                name: 'objectCost',
                rightAdornment: '₽',
                hidden: state => state.calculateType !== ECalculateType.CostOfRealEstate,
                validation: {
                  min: {
                    message: 'Стоимость объекта должна быть больше 1 ₽',
                    value: 2,
                  },
                  max: {
                    message: 'Стоимость объекта не может превышать 999 999 999 ₽',
                    value: 999999999,
                  },
                  required: 'Поле должно быть заполнено',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Первоначальный взнос',
                placeholder: '1 200 000',
                type: EFieldType.InputWithOptions,
                name: 'downPayment',
                decimalDigits: 2,
                rightAdornment: '₽',
                source: state => {
                  const options = downPaymentOptions.map(option => ({
                    label: `${option} %`,
                    value: `${option}`,
                    checked: calculatePercentage(state.objectCost, option) === state.downPayment,
                    hidden: state.calculateType !== ECalculateType.CostOfRealEstate,
                  }));

                  return layout === 'small' ? options.slice(0, 4) : options;
                },
                calculateForField: (value, state) => {
                  if (state && state.objectCost) {
                    return calculatePercentage(state.objectCost, value);
                  }

                  return 0;
                },
                validation: {
                  validate: (value, state) => {
                    if (state.objectCost === undefined && state.calculateType === ECalculateType.CostOfRealEstate) {
                      return 'Сначала необходимо указать стоимость объекта';
                    }
                    if (Number(value) >= state.objectCost) {
                      return 'Первоначальный взнос должен быть меньше стоимости объекта';
                    }

                    return undefined;
                  },
                  min: {
                    message: 'Первоначальный взнос должен быть больше 1 ₽',
                    value: 2,
                  },
                  required: 'Поле должно быть заполнено',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Срок кредита',
                placeholder: '5 лет',
                type: EFieldType.InputWithOptions,
                name: 'loanTerm',
                rightAdornment: 'лет',
                source: [
                  { label: '10 лет', value: '10' },
                  { label: '15 лет', value: '15' },
                  { label: '20 лет', value: '20' },
                  ...(layout === 'normal' ? [{ label: '25 лет', value: '25' }] : []),
                  { label: '30 лет', value: '30' },
                ],
                validation: {
                  min: {
                    message: 'Срок кредита не может быть меньше 1 года',
                    value: 1,
                  },
                  max: {
                    message: 'Срок кредита не может превышать 35 лет',
                    value: 35,
                  },
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Программа',
                type: EFieldType.Chips,
                offset: { bottom: 12 },
                name: 'benefitsProgram',
                source: state => {
                  const gosProgramDisable =
                    (isNewBuilding(state) && !isFlatOrUnselected(state)) ||
                    isSecondary(state) ||
                    state.mortgageType === EMortgageType.Refinancing;
                  const familyProgramDisable =
                    isSecondary(state) ||
                    (isRefinance(state) && isNewBuilding(state) && !isFlatOrUnselected(state)) ||
                    (isNewBuilding(state) && isApartmentsOrCarPlace(state));
                  const ItProgramDisable =
                    isSecondary(state) || isRefinance(state) || (isNewBuilding(state) && isApartmentsOrCarPlace(state));

                  return [
                    {
                      label: 'Без господдержки',
                      value: EBenefitProgram.NoProgram,
                    },
                    {
                      label: 'Льготная',
                      value: EBenefitProgram.GovernmentSupport2020,
                      disabled: gosProgramDisable,
                    },
                    { label: 'Семейная', value: EBenefitProgram.Family, disabled: familyProgramDisable },
                    { label: 'IT ипотека', value: EBenefitProgram.It, disabled: ItProgramDisable },
                  ];
                },
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Static,
                label: 'Особенности программ',
                type: EStaticType.ProgramFeaturesButton,
              },
            ],
          },
          {
            category: EElementCategory.Group,
            label: 'Данные заёмщика',
            elements: [
              {
                category: EElementCategory.Field,
                label: 'Тип занятости',
                type: EFieldType.Chips,
                name: 'typeOfEmployment',
                source: [
                  { label: 'Наемный работник', value: EEmploymentType.Employee },
                  { label: 'Самозанятый', value: EEmploymentType.SelfEmployed },
                  { label: 'ИП', value: EEmploymentType.Entrepreneur },
                  { label: 'Собственник бизнеса', value: EEmploymentType.BusinessOwner },
                ],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                type: EFieldType.Radio,
                name: 'incomeConfirm',
                label: 'Подтверждение дохода',
                source: [
                  {
                    label: 'С подтверждением дохода',
                    value: EIncomeConfirmation.Confirmation,
                    description: 'По документам на выбор: 2-НДФЛ, 3-НДФЛ, справке по форме банка или выписке из СФР',
                  },
                  {
                    label: 'Без подтверждения дохода',
                    value: EIncomeConfirmation.NoConfirmation,
                    description: 'По двум документам: паспорт и СНИЛС',
                  },
                ],
                validation: {
                  required: 'Выберите значение',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Возраст заёмщика',
                type: EFieldType.InputNumber,
                name: 'age',
                placeholder: '30',
                rightAdornment: 'лет',
                validation: {
                  min: {
                    message: 'Минимальный возраст заёмщика не менее 18 лет',
                    value: 18,
                  },
                  max: {
                    message: 'Максимальный возраст заёмщика не более 85 лет',
                    value: 85,
                  },
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Зарплатный проект',
                type: EFieldType.Select,
                name: 'payrollBank',
                // обогащается после получения данных с сервера
                source: [],
                placeholder: 'Нет зарплатного проекта',
                disabled: state => state.typeOfEmployment === EEmploymentType.SelfEmployed,
              },
              {
                category: EElementCategory.Field,
                label: 'Общий трудовой стаж',
                placeholder: ['5', '5'],
                rightAdornment: ['лет', 'мес.'],
                type: EFieldType.YearMonthField,
                name: 'totalWorkExperience',
                validation: {
                  max: {
                    message: 'Общий трудовой стаж не может превышать 60 лет',
                    value: MAX_VALUE_IN_MONTH,
                  },
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Стаж на текущем месте',
                type: EFieldType.YearMonthField,
                name: 'currentWorkExperience',
                placeholder: ['5', '5'],
                rightAdornment: ['лет', 'мес.'],
                validation: {
                  validate: (value, state) => {
                    if (state.totalWorkExperience && Number(value) > state.totalWorkExperience) {
                      return 'Стаж работы на текущем месте не может превышать общий стаж работы';
                    }

                    return undefined;
                  },
                  max: {
                    message: 'Стаж на текущем месте не может превышать 60 лет',
                    value: MAX_VALUE_IN_MONTH,
                  },
                },
                hidden: state =>
                  state.typeOfEmployment === EEmploymentType.BusinessOwner ||
                  state.typeOfEmployment === EEmploymentType.Entrepreneur,
              },
              {
                category: EElementCategory.Field,
                label: 'Срок владения бизнесом',
                type: EFieldType.YearMonthField,
                name: 'termOfBusinessOwnership',
                placeholder: ['5', '7'],
                rightAdornment: ['лет', 'мес.'],
                validation: {
                  validate: (value, state) => {
                    if (state.totalWorkExperience && Number(value) > state.totalWorkExperience) {
                      return 'Срок владения бизнесом не может превышать общий стаж работы';
                    }

                    return undefined;
                  },
                  max: {
                    message: 'Срок владения бизнесом не может превышать 60 лет',
                    value: MAX_VALUE_IN_MONTH,
                  },
                },
                hidden: state =>
                  state.typeOfEmployment === EEmploymentType.Employee ||
                  state.typeOfEmployment === EEmploymentType.SelfEmployed ||
                  state.typeOfEmployment === EEmploymentType.Entrepreneur ||
                  !state.typeOfEmployment,
              },
              {
                category: EElementCategory.Field,
                label: 'Срок владения ИП',
                type: EFieldType.YearMonthField,
                name: 'termOfEntrepreneurship',
                placeholder: ['5', '7'],
                rightAdornment: ['лет', 'мес.'],
                validation: {
                  validate: (value, state) => {
                    if (state.totalWorkExperience && Number(value) > state.totalWorkExperience) {
                      return 'Срок владения ИП не может превышать общий стаж работы';
                    }

                    return undefined;
                  },
                  max: {
                    message: 'Срок владения ИП не может превышать 60 лет',
                    value: MAX_VALUE_IN_MONTH,
                  },
                },
                hidden: state =>
                  state.typeOfEmployment === EEmploymentType.Employee ||
                  state.typeOfEmployment === EEmploymentType.SelfEmployed ||
                  state.typeOfEmployment === EEmploymentType.BusinessOwner ||
                  !state.typeOfEmployment,
              },
              {
                category: EElementCategory.Field,
                label: 'Доля в бизнесе',
                type: EFieldType.InputNumber,
                name: 'shareOfBusiness',
                placeholder: '5',
                rightAdornment: '%',
                decimalDigits: 2,
                validation: {
                  min: {
                    message: 'Доля в бизнесе не может быть меньше 0.01%',
                    value: 0.01,
                  },
                  max: {
                    message: 'Доля в бизнесе не может превышать 100%',
                    value: 100,
                  },
                },
                hidden: state => state.typeOfEmployment !== 'businessOwner',
              },
              {
                category: EElementCategory.Field,
                label: 'Страхование жизни и здоровья',
                type: EFieldType.Switch,
                name: 'hasLifeInsurance',
              },
            ],
          },
        ],
      }) satisfies IScheme,
    [downPaymentOptions, layout],
  );
};
